import gql from 'graphql-tag'
import client from '@stores/apollo'
import { get } from 'lodash'
import { IImportResult } from './upload'
import { IEdge, IPageInfo } from '@api/types'

interface IProps {
  after: string
}

export interface IHistory {
  after: string
  results: IImportResult[]
  page: IPageInfo
  isLoading: boolean
  isPaging: boolean
}

interface Payload {
  data: {
    viewer: {
      company: BuildingImportPayload | OperationCostImportPayload | LeaseImportPayload
    }
  }
}

interface BuildingImportPayload {
  buildingImports: IEdge<IImportResult>[]
  counters: {
    total: number
  }
  pageInfo: IPageInfo
}

interface OperationCostImportPayload {
  operationCostImports: IEdge<IImportResult>[]
  counters: {
    total: number
  }
  pageInfo: IPageInfo
}

interface LeaseImportPayload {
  leaseImports: IEdge<IImportResult>[]
  counters: {
    total: number
  }
  pageInfo: IPageInfo
}

export default function history(
  { after = '' }: IProps,
  type: string
): Promise<{ nodes: IImportResult[]; pageInfo: IPageInfo }> {
  const model = models[type]
  const variables = { after }

  return client.gql.query({ query: model.query, variables }).then(({ data }: Payload) => {
    const nodes = get(data, model.path + '.edges', []).map((e: IEdge<IImportResult>) => e.node)
    return {
      nodes,
      pageInfo: get(data, model.path + '.pageInfo'),
    }
  })
}

interface IModel {
  path: string
  query: any
}

const opcostHistoryQuery = gql`
  query OperationCostHistory($after: String) {
    viewer {
      company {
        operation_cost_imports(first: 15, after: $after) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              id
              createdAt
              importedBy
              status
              totalCount
              updatedCount
              createdCount
              failedCount
              url
            }
          }
        }
      }
    }
  }
`

export const buildingImportsQuery = gql`
  query BuildingImportsHistory($after: String) {
    viewer {
      company {
        buildingImports(first: 15, after: $after) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              id
              createdAt
              importedBy
              status
              totalCount
              updatedCount
              createdCount
              failedCount
              url
            }
          }
        }
      }
    }
  }
`

export const floorImportsQuery = gql`
  query FloorImportsHistory($after: String) {
    viewer {
      company {
        floorImports(first: 15, after: $after) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              id
              createdAt
              importedBy
              status
              totalCount
              updatedCount
              createdCount
              failedCount
              url
            }
          }
        }
      }
    }
  }
`

export const leaseImportsQuery = gql`
  query LeaseImportsHistory($after: String) {
    viewer {
      company {
        leaseImports(first: 15, after: $after) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              id
              createdAt
              importedBy
              status
              totalCount
              createdCount
              failedCount
              url
            }
          }
        }
      }
    }
  }
`

export const employeesTagsImportsQuery = gql`
  query EmployeesTagsImportsHistory($after: String) {
    viewer {
      company {
        employeesTagsImports(first: 15, after: $after) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              id
              createdAt
              importedBy
              status
              totalCount
              createdCount
              failedCount
              url
            }
          }
        }
      }
    }
  }
`

export const spacesTagsImportsQuery = gql`
  query SpacesTagsImportsHistory($after: String) {
    viewer {
      company {
        spacesTagsImports(first: 15, after: $after) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              id
              createdAt
              importedBy
              status
              totalCount
              createdCount
              failedCount
              url
            }
          }
        }
      }
    }
  }
`

const models: { [s: string]: IModel } = {
  operationCost: {
    path: 'viewer.company.operation_cost_imports',
    query: opcostHistoryQuery,
  },
  leaseImports: {
    path: 'viewer.company.leaseImports',
    query: leaseImportsQuery,
  },
  employeesTagsImports: {
    path: 'viewer.company.employeesTagsImports',
    query: employeesTagsImportsQuery,
  },
  buildingImports: {
    path: 'viewer.company.buildingImports',
    query: buildingImportsQuery,
  },
  floorImports: {
    path: 'viewer.company.floorImports',
    query: floorImportsQuery,
  },
  spacesTagsImports: {
    path: 'viewer.company.spacesTagsImports',
    query: spacesTagsImportsQuery,
  },
}
